import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import {
  Form,
  Input,
  Button,
  Checkbox,
  message,
  Modal,
  Radio,
  Typography,
  Tooltip
} from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import useAuthContext from '../../contexts/AuthContext';
import CGU from '../../assets/Assurmax_CGU.pdf';

const CGU_CURRENT_VERSION_DATE = new Date('2026-03-10T00:00:00.000Z');

const LoginForm = ({
  switchForm,
  currentForm,
  mail,
  setMail,
  setCurrent,
  setInsurer
}) => {
  const location = useLocation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { Title } = Typography;
  const { isValid, dispatchAPI, remember } = useAuthContext();
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const { from } = (location && location.state) || { from: { pathname: '/' } };
  const [openMailField, setOpenMailField] = useState(false);
  const [visible, setVisible] = useState(false);
  const [userType, setUserType] = useState('');
  const [cguModalMode, setCguModalMode] = useState('first'); // 'first' | 'update'
  const [pendingLoginValues, setPendingLoginValues] = useState(null);

  const loginUser = async (email, password, rememberMe) => {
    setLoading(true);
    try {
      await dispatchAPI('LOGIN', {
        email,
        password,
        rememberMe
      });
      message.success(t('login.success'));
    } catch (e) {
      if (e?.response?.status === 401) {
        message.error(t('login.login_error'));
      } else if (e.response) {
        if (e?.response?.data?.description) {
          message.error(e.response.data.description);
        } else {
          message.error(t('login.wrongDetails'));
        }
      } else if (e?.request) {
        message.error(t('login.networkError'));
      } else {
        message.error(t('login.error'));
      }
      setLoading(false);
    }
  };

  const postGeneralConditionAccepted = async (userEmail) => {
    try {
      await dispatchAPI('POST', {
        url: '/login/generalCondition',
        body: { email: userEmail }
      });
    } catch {
      message.warning(t('login.cgu_save_error'));
    }
  };

  const checkGeneralCondition = async (userEmail) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/login/generalCondition?email=${userEmail}`
      });
      if (!data.accepted) return 'first';
      if (new Date(data.date) < CGU_CURRENT_VERSION_DATE) return 'update';
      return 'ok';
    } catch {
      message.warning(t('login.error'));
      return 'ok';
    }
  };

  const handleModalClose = () => {
    setVisible(false);
    setPendingLoginValues(null);
    setLoading(false);
    cguModalMode === 'first'
      ? message.error(t('login.generalCondition_failed'))
      : message.error(t('login.generalCondition_update_failed'));
  };

  const handleCguAccepted = async () => {
    if (!pendingLoginValues) return;
    const { username, password, remember: rememberMe } = pendingLoginValues;
    setVisible(false);
    await postGeneralConditionAccepted(username.toLowerCase());
    await loginUser(username.toLowerCase(), password, rememberMe);
    setPendingLoginValues(null);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const userEmail = values.username.toLowerCase();
    const cguStatus = await checkGeneralCondition(userEmail);
    if (cguStatus === 'ok') {
      await loginUser(userEmail, values.password, values.remember);
    } else {
      setPendingLoginValues(values);
      setCguModalMode(cguStatus);
      setVisible(true);
      setLoading(false);
    }
  };

  const getInsurerValidationStep = async () => {
    try {
      const insurer = await dispatchAPI('GET', {
        url: `/register/mail?mail=${mail}`
      });
      if (
        insurer &&
        insurer.data.status === 'En cours de codage' &&
        insurer.data.validation_step !== 2
      ) {
        await setCurrent(insurer.data.validation_step + 1);
        switchForm('register');
      } else {
        message.success(t('login.already_registered'));
        message.success(t('login.check_email'));
      }
      setInsurer(insurer.data);
    } catch (e) {
      if (e.response) message.error(t('login.wrongDetails'));
    }
  };

  useEffect(() => {
    const params = location.search.split('?')[1];
    const objectParams = new URLSearchParams(params);
    const values = objectParams.values();
    const outputValue = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const value of values) {
      outputValue.push(value);
    }
    if (outputValue.length !== 0) {
      if (outputValue[0] === 'failed') {
        // The signing ceremony failed
        message.warning(t('login.universign_failed'));
      } else if (outputValue[0] === 'success') {
        // The signing ceremony succeeded
        message.success(t('login.universign_success'));
      }
      navigate(0);
    }
    // eslint-disable-next-line
  }, [location]);

  const isReductScreen = useMediaQuery({ query: '(max-width: 992px)' });

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      initialValues={{ remember }}
      className="login-form"
      style={{
        background: isReductScreen
          ? 'rgba(51, 51, 51, 0.6)'
          : 'rgba(51, 51, 51, 0.4)',
        padding: ' 30px 40px 0 40px',
        borderRadius: '15px'
      }}
    >
      <Title
        style={{
          textAlign: 'center',
          marginBottom: 40,
          fontSize: 26,
          color: 'var(--componentBackground)'
        }}
      >
        {t(`login.title.${currentForm}`)}
      </Title>
      {isValid && <Navigate to={{ ...from, from: 'login' }} replace />}
      <Radio.Group buttonStyle="solid" className="login-radio-group">
        <Radio.Button value="a" onClick={() => setUserType('customer')}>
          {t('login.customer')}
        </Radio.Button>
        <Radio.Button value="b" onClick={() => setUserType('insurer')}>
          {t('login.insurer')}
        </Radio.Button>
      </Radio.Group>
      {userType && (
        <>
          <Form.Item
            name="username"
            rules={[{ required: true, message: t('login.usernameMissing') }]}
          >
            <Input
              prefix={
                <UserOutlined
                  style={{ color: 'var(--borderColor)', marginRight: 6 }}
                />
              }
              placeholder={t('login.username')}
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: t('login.pwdMissing') }]}
          >
            <Input.Password
              prefix={
                <LockOutlined
                  style={{ color: 'var(--borderColor)', marginRight: 6 }}
                />
              }
              placeholder={t('login.password')}
            />
          </Form.Item>
          <Form.Item style={{ textAlign: 'left', margin: '24px 0' }}>
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox>
                <span
                  style={{
                    color: 'var(--textColorSecondary)'
                  }}
                >
                  {t('login.rememberMe')}
                </span>
              </Checkbox>
            </Form.Item>
            <Button
              type="link"
              style={{
                float: 'right',
                padding: 0,
                height: 22,
                fontFamily: 'PT Sans',
                textTransform: 'none',
                letterSpacing: '0',
                color: 'var(--textColorSecondary)'
              }}
              onClick={() => switchForm('forgotPwd')}
            >
              {t('login.forgotPwd')}
            </Button>
          </Form.Item>
          <Tooltip title={t('buttons.login_insurer')}>
            <Form.Item style={{ marginBottom: 16 }}>
              <Button
                type="default"
                htmlType="submit"
                style={{ width: isReductScreen ? '60%' : '50%' }}
                loading={isLoading}
              >
                {t('login.connect')}
              </Button>
            </Form.Item>
          </Tooltip>
        </>
      )}
      {userType === 'insurer' && (
        <Tooltip title={t('buttons.become_partner')}>
          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="default"
              style={{ width: isReductScreen ? '60%' : '50%' }}
              loading={isLoading}
              onClick={() => switchForm('register')}
            >
              {t('login.register')}
            </Button>
          </Form.Item>
        </Tooltip>
      )}
      {userType === 'insurer' && (
        <Tooltip title={t('buttons.continue_registration')}>
          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="link"
              style={{
                color: 'var(--textColorSecondary)',
                width: isReductScreen ? '60%' : '50%'
              }}
              loading={isLoading}
              onClick={() => setOpenMailField(!openMailField)}
            >
              {t('login.register_step')}
            </Button>
          </Form.Item>
        </Tooltip>
      )}
      {userType === 'insurer' && openMailField ? (
        <>
          <Form.Item
            name="email_step"
            rules={[
              {
                required: true,
                message: t('errors.form.REQUIRED'),
                type: 'email'
              }
            ]}
          >
            <Input
              placeholder={t('insurers.form.owner_email')}
              onChange={(event) => setMail(event.target.value)}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              style={{ width: 200 }}
              loading={isLoading}
              onClick={() => {
                getInsurerValidationStep();
              }}
            >
              {t('login.confirmation_email')}
            </Button>
          </Form.Item>
        </>
      ) : (
        []
      )}
      {userType === 'customer' && (
        <>
          <Form.Item style={{ marginTop: 0 }}>
            <Button
              type="link"
              style={{
                color: 'var(--textColorSecondary)'
              }}
              onClick={() => switchForm('firstLoginCustomer')}
            >
              {t('login.customerForm')}
            </Button>
          </Form.Item>
        </>
      )}
      <Modal
        title={
          cguModalMode === 'update'
            ? t('login.cgu_modal_title_update')
            : t('login.cgu_modal_title_first')
        }
        open={visible}
        onOk={handleCguAccepted}
        onCancel={handleModalClose}
        cancelText={t('login.refused')}
        okText={
          cguModalMode === 'update'
            ? t('login.cgu_accept_update')
            : t('login.cgu_accept_first')
        }
        width={1200}
        closable={false}
      >
        <p style={{ marginBottom: 16 }}>
          {cguModalMode === 'update'
            ? t('login.cgu_modal_description_update')
            : t('login.cgu_modal_description_first')}
        </p>
        <iframe height="900" width="100%" title="CGU" src={CGU} />
      </Modal>
    </Form>
  );
};

LoginForm.propTypes = {
  switchForm: PropTypes.func.isRequired,
  setMail: PropTypes.func.isRequired,
  mail: PropTypes.string.isRequired,
  setCurrent: PropTypes.func.isRequired,
  setInsurer: PropTypes.func.isRequired,
  currentForm: PropTypes.string.isRequired
};

export default LoginForm;
